/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* ===========================================================================
 * TIS custom product-page components
 * ========================================================================= */
(function () {
  'use strict';

  /* -- Shared money formatter (guards for Shopify.formatMoney) ----------------
     The fallback runs whenever Shopify.formatMoney or theme.moneyFormat is missing, which is
     often enough that it has to be presentable: it groups thousands with commas, so a printer
     configuration reads "$1,234.56" rather than "$1234.56". */
  function tisFormatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }

    const negative = cents < 0;
    const parts = (Math.abs(cents) / 100).toFixed(2).split('.');
    const grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return (negative ? '-$' : '$') + grouped + '.' + parts[1];
  }

  /* GST is 10%, but whether a stored price contains it depends on the store's "All prices
     include tax" setting and the variant's own taxable flag. These mirror
     snippets/tis-price-ex-gst.liquid and tis-price-inc-gst.liquid exactly — same conditions,
     same rounding — so a figure recalculated here can't drift from the one Liquid rendered.

     taxable defaults to true: callers that genuinely have no variant behind a figure get the
     ordinary case, as they do server-side. */
  function tisExGstCents(cents, taxable, taxesIncluded) {
    if (taxable === false || !taxesIncluded) return cents;
    return Math.round(cents / 1.1);
  }

  function tisIncGstCents(cents, taxable, taxesIncluded) {
    if (taxable === false || taxesIncluded) return cents;
    return Math.round(cents * 1.1);
  }

  /* Reads a data attribute written by Liquid as "true"/"false". */
  function tisFlag(el, name, fallback) {
    if (!el || !el.hasAttribute(name)) return fallback;
    return el.getAttribute(name) !== 'false';
  }

  /* Request a quote (snippets/tis-quote-button.liquid). Hands the configured lines to Fish
     Wishlist's add-to-list drawer in bulk mode — the same path the app's own "save cart to
     list" button takes — so the customer picks or creates a list, every line is written with
     its quantity in one go, and the list's Request a quote button takes it from there.

     Returns false when the drawer isn't on the page (app embed off, or not booted yet), so the
     caller can say so rather than the click doing nothing. */
  let tisQuoteNameObserver = null;

  function tisOpenQuoteList(trigger, items) {
    const app = window.FishWishlist;
    const drawer = app && app.elements && app.elements.addToWishlistDrawer;
    if (!drawer || typeof drawer.openBulk !== 'function') return false;

    drawer.openBulk(trigger, items.map((item) => ({
      variant_id: item.id,
      quantity: item.quantity,
      properties: item.properties || {}
    })));
    tisNameQuoteList(drawer, trigger.getAttribute('data-list-name'));
    return true;
  }

  /* openBulk() pre-fills the new-list name with the app's default ("My List"). A list made from
     this button is a quote, so it is named for one instead — then put back when the drawer
     closes, so the next heart click gets the app's default again. */
  function tisNameQuoteList(drawer, listName) {
    const form = drawer.newWishlistForm;
    const input = form && form.input;
    if (!input || !listName) return;

    if (tisQuoteNameObserver) tisQuoteNameObserver.disconnect();
    const appPlaceholder = input.placeholder;

    input.value = listName;
    input.placeholder = listName;
    if (form.button) form.button.disabled = false;

    tisQuoteNameObserver = new MutationObserver(() => {
      if (drawer.dataset.open !== 'false') return;
      tisQuoteNameObserver.disconnect();
      tisQuoteNameObserver = null;

      input.placeholder = appPlaceholder;
      /* Left alone if the customer typed their own name and the drawer closed unsaved. */
      if (input.value === listName) input.value = '';
      if (typeof form.setDefaultTitle === 'function') form.setDefaultTitle();
    });
    tisQuoteNameObserver.observe(drawer, { attributes: true, attributeFilter: ['data-open'] });
  }

  /* -- <tis-product-tabs>: accessible client-side tab switching -------------- */
  class TisProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => this.onKeydown(e));
      });
    }

    select(tab) {
      this.tabs.forEach((t) => {
        const selected = t === tab;
        t.classList.toggle('is-active', selected);
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const show = panel.id === tab.getAttribute('aria-controls');
        panel.classList.toggle('is-hidden', !show);
        panel.hidden = !show;
      });
    }

    onKeydown(e) {
      const i = this.tabs.indexOf(e.currentTarget);
      if (i === -1) return;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = this.tabs[(i + 1) % this.tabs.length];
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      else if (e.key === 'Home') next = this.tabs[0];
      else if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (next) {
        e.preventDefault();
        this.select(next);
        next.focus();
      }
    }
  }
  if (!customElements.get('tis-product-tabs')) {
    customElements.define('tis-product-tabs', TisProductTabs);
  }

  /* -- <tis-order-form>: multi-line add-to-cart ------------------------------ */
  class TisOrderForm extends HTMLElement {
    connectedCallback() {
      this.addUrl = (window.routes && window.routes.cartAddUrl) || this.getAttribute('data-add-url') || '/cart/add';
      this.totalEl = this.querySelector('[data-of-total]');
      this.addBtn = this.querySelector('[data-of-add]');
      this.quoteBtn = this.querySelector('[data-quote-btn]');
      this.statusEl = this.querySelector('[data-of-status]');
      this.inputs = Array.from(this.querySelectorAll('.tis-of__qty'));
      this.taxesIncluded = tisFlag(this, 'data-taxes-included', true);

      /* Optional slitting upsell — absent on most products (see tis-order-form.liquid). */
      this.slitting = this.querySelector('[data-of-slitting]');
      this.slittingToggle = this.querySelector('[data-of-slitting-toggle]');
      this.slittingField = this.querySelector('[data-of-slitting-field]');
      this.slittingCut = this.querySelector('[data-of-slitting-cut]');
      this.slittingError = this.querySelector('[data-of-slitting-error]');
      this.slittingSummary = this.querySelector('[data-of-slitting-summary]');

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        if (e.target.classList.contains('tis-of__qty')) this.onQtyChange(e.target);
        if (e.target === this.slittingToggle) this.onSlittingToggle();
      });
      this.addEventListener('input', (e) => {
        if (e.target.classList.contains('tis-of__qty')) this.onQtyChange(e.target);
        /* Clear a "required" message as soon as the customer starts typing a width. */
        if (e.target === this.slittingCut && this.slittingCut.value.trim()) this.showSlittingError('');
      });

      /* The order form sits inside the theme's {% form 'product' %}, so Enter in a text field
         triggers implicit submission — a full-page POST to /cart/add that adds the hidden
         variant id alone and loses the whole order. Enter here means "I'm done typing". */
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        if (e.target === this.slittingCut || e.target.classList.contains('tis-of__qty')) {
          e.preventDefault();
        }
      });

      this.recalculate();
    }

    onClick(e) {
      const trigger = e.target.closest('[data-action]');
      if (!trigger || !this.contains(trigger)) return;
      const action = trigger.getAttribute('data-action');

      if (action === 'of-increase' || action === 'of-decrease') {
        const input = trigger.parentElement.querySelector('.tis-of__qty');
        if (!input) return;
        const step = action === 'of-increase' ? 1 : -1;
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        this.onQtyChange(input);
      } else if (action === 'of-add') {
        this.addAll();
      } else if (action === 'request-quote') {
        this.requestQuote(trigger);
      }
    }

    onQtyChange(input) {
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 0) val = 0;
      input.value = val;
      const row = input.closest('[data-variant-row]');
      if (row) row.classList.toggle('is-active', val > 0);
      this.recalculate();
      // Notify a parent (e.g. <tis-printer-buybox>) that an embedded row changed.
      this.dispatchEvent(new CustomEvent('tis-of:change', { bubbles: true }));
    }

    onSlittingToggle() {
      const on = this.slittingWanted();
      if (this.slittingField) this.slittingField.hidden = !on;
      if (!on) this.showSlittingError('');
      if (on && this.slittingCut) this.slittingCut.focus();
      this.recalculate();
    }

    slittingWanted() {
      return !!(this.slittingToggle && this.slittingToggle.checked);
    }

    /* Ex GST, matching the per-roll figure quoted on the label beside it. The line total is
       converted from the whole amount rather than multiplying a converted unit price, so it
       rounds to the same cent the cart will charge. */
    renderSlittingSummary(quantity) {
      if (!this.slittingSummary) return;

      const show = this.slittingWanted() && quantity > 0;
      this.slittingSummary.hidden = !show;
      if (!show) {
        this.slittingSummary.textContent = '';
        return;
      }

      const unit = parseInt(this.slitting.getAttribute('data-price'), 10) || 0;
      const taxable = tisFlag(this.slitting, 'data-taxable', true);
      const unitEx = tisExGstCents(unit, taxable, this.taxesIncluded);
      const lineEx = tisExGstCents(unit * quantity, taxable, this.taxesIncluded);

      this.slittingSummary.textContent =
        quantity + ' \u00d7 ' + tisFormatMoney(unitEx) + ' = ' + tisFormatMoney(lineEx);
    }

    showSlittingError(message) {
      if (!this.slittingError) return;
      this.slittingError.textContent = message;
      this.slittingError.hidden = !message;
      if (this.slittingCut) this.slittingCut.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    recalculate() {
      let totalCents = 0;
      let totalQty = 0;
      /* The footer total is labelled "Tax included", so each line is converted before summing —
         a GST-free line contributes its bare price, a taxable one its inc-GST price. */
      this.inputs.forEach((input) => {
        const qty = parseInt(input.value, 10) || 0;
        totalQty += qty;
        const line = qty * (parseInt(input.getAttribute('data-price'), 10) || 0);
        totalCents += tisIncGstCents(line, tisFlag(input, 'data-taxable', true), this.taxesIncluded);
      });

      /* Slitting is charged per roll, so its quantity is the roll total rather than a figure the
         customer sets. totalQty is final by this point — the loop above has run. */
      if (this.slittingWanted()) {
        const unit = parseInt(this.slitting.getAttribute('data-price'), 10) || 0;
        const taxable = tisFlag(this.slitting, 'data-taxable', true);
        totalCents += tisIncGstCents(unit * totalQty, taxable, this.taxesIncluded);
      }
      this.renderSlittingSummary(totalQty);

      if (this.totalEl) this.totalEl.textContent = tisFormatMoney(totalCents);
      /* Slitting alone is not a purchase — there has to be something to slit. */
      if (this.addBtn) this.addBtn.disabled = totalQty === 0;
      if (this.quoteBtn) this.quoteBtn.disabled = totalQty === 0;
    }

    /* The lines to add, shared by add-to-cart and request-a-quote so a quote is for exactly
       what the cart would have received. Returns null when there is nothing to add, or when
       slitting is ticked without a width (the error is shown here). */
    collectItems() {
      const items = this.inputs
        .map((input) => ({ id: parseInt(input.getAttribute('data-variant-id'), 10), quantity: parseInt(input.value, 10) || 0 }))
        .filter((item) => item.quantity > 0);
      if (!items.length) return null;

      if (this.slittingWanted()) {
        const cut = this.slittingCut ? this.slittingCut.value.trim() : '';

        /* Refused here rather than server-side: /cart/add.js would happily accept a slitting
           line with no width, and it can't be fulfilled without one. */
        if (!cut) {
          this.showSlittingError(this.slitting.getAttribute('data-error-required') || 'Required');
          if (this.slittingCut) this.slittingCut.focus();
          return null;
        }

        const slittingId = parseInt(this.slitting.getAttribute('data-variant-id'), 10);
        /* One slitting unit per roll. items were filtered to quantity > 0 above, so this is the
           number of rolls actually being ordered. */
        const slittingQty = items.reduce((sum, item) => sum + item.quantity, 0);

        if (slittingId && slittingQty > 0) {
          const properties = {};
          properties[this.slitting.getAttribute('data-property-cut') || 'Cut to (mm)'] = cut;

          const parentSku = this.slitting.getAttribute('data-parent-sku');
          if (parentSku) properties[this.slitting.getAttribute('data-property-sku') || 'For product'] = parentSku;

          /* Leading underscore: hidden from the customer, and what the cart page keys on to
             suppress this line's quantity picker (see main-cart.liquid). */
          properties._slitting = 'true';

          items.push({ id: slittingId, quantity: slittingQty, properties: properties });
        }
      }

      return items;
    }

    requestQuote(trigger) {
      const items = this.collectItems();
      if (!items) return;

      if (this.statusEl) this.statusEl.innerHTML = '';
      if (!tisOpenQuoteList(trigger, items)) this.showError(trigger.getAttribute('data-error'));
    }

    addAll() {
      const items = this.collectItems();
      if (!items) return;

      if (this.statusEl) this.statusEl.innerHTML = '';
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      fetch(this.addUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
        .then((result) => {
          this.addBtn.classList.remove('is-loading');
          if (result.ok) {
            const addedQty = items.reduce((sum, item) => sum + item.quantity, 0);
            document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: addedQty, productAdded: true } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            // Reset quantities after a successful add.
            this.inputs.forEach((input) => {
              input.value = 0;
              const row = input.closest('[data-variant-row]');
              if (row) row.classList.remove('is-active');
            });
            /* Slitting resets with them — leaving it ticked would silently add a second
               slitting line, with a stale width, on the next add. */
            if (this.slittingToggle) {
              this.slittingToggle.checked = false;
              if (this.slittingCut) this.slittingCut.value = '';
              this.onSlittingToggle();
            }
            this.recalculate();
          } else {
            this.showError((result.data && result.data.description) || 'Unable to add to cart.');
            this.addBtn.disabled = false;
          }
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        })
        .catch(() => {
          this.addBtn.classList.remove('is-loading');
          this.addBtn.disabled = false;
          this.showError('Unable to add to cart.');
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        });
    }

    showError(message) {
      if (this.statusEl) this.statusEl.innerHTML = '<span class="tis-of__error">' + message + '</span>';
    }
  }
  if (!customElements.get('tis-order-form')) {
    customElements.define('tis-order-form', TisOrderForm);
  }

  /* -- Savings badge, re-totalled from the order form ------------------------
     On the standard product template the savings badge sits in the product-meta block, above the
     order form, so the order form element can't reach it — but the total saved depends on which
     quantities are chosen. This listens for the change event the order form bubbles and rewrites
     the badge with the ex-GST total across the selection, falling back to the per-unit saving
     when nothing is selected.

     Embedded order forms are skipped: those belong to the printer buy box, whose own price
     block states the discount as a struck-through regular price instead. */
  document.addEventListener('tis-of:change', (event) => {
    const orderForm = event.target;
    if (!orderForm || orderForm.hasAttribute('data-embedded')) return;

    const badge = document.querySelector('[data-meta-savings]');
    const valueEl = badge && badge.querySelector('[data-meta-savings-value]');
    /* Absent in percentage mode, where a per-quantity total would be meaningless. */
    if (!valueEl) return;

    let saved = 0;
    let totalQty = 0;

    orderForm.querySelectorAll('.tis-of__qty').forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      const price = parseInt(input.getAttribute('data-price'), 10) || 0;
      const compare = parseInt(input.getAttribute('data-compare-price'), 10) || 0;
      totalQty += qty;
      saved += Math.max(0, compare - price) * qty;
    });

    if (totalQty === 0) {
      saved = parseInt(badge.getAttribute('data-unit-savings'), 10) || 0;
    }

    valueEl.textContent = tisFormatMoney(tisExGstCents(saved));
    /* Clears the inline display:none Liquid sets when the default variant isn't discounted —
       rows further down the table may still be. */
    badge.style.display = saved > 0 ? '' : 'none';
  });

  /* -- <tis-printer-buybox>: printer configurator + multi-line add ----------- */
  class TisPrinterBuybox extends HTMLElement {
    connectedCallback() {
      this.addUrl = (window.routes && window.routes.cartAddUrl) || this.getAttribute('data-add-url') || '/cart/add';
      this.printerId = parseInt(this.getAttribute('data-printer-variant-id'), 10);
      this.printerPrice = parseInt(this.getAttribute('data-printer-price'), 10) || 0;
      this.printerComparePrice = parseInt(this.getAttribute('data-printer-compare-price'), 10) || 0;
      this.taxesIncluded = tisFlag(this, 'data-taxes-included', true);
      this.printerTaxable = tisFlag(this, 'data-printer-taxable', true);
      this.printerAvailable = tisFlag(this, 'data-printer-available', true);
      this.soldOutLabel = this.getAttribute('data-sold-out-label') || 'Sold out';
      this.accessoriesSection = this.querySelector('[data-bb-accessories]');
      this.ctaLabelEl = this.querySelector('[data-bb-cta-label]');
      this.addBtn = this.querySelector('[data-bb-add]');
      this.quoteBtn = this.querySelector('[data-quote-btn]');
      this.statusEl = this.querySelector('[data-bb-status]');
      this.track = this.querySelector('[data-bb-track]');
      this.savingsEl = this.querySelector('[data-bb-savings]');
      this.savingsValueEl = this.querySelector('[data-bb-savings-value]');
      this.compareEl = this.querySelector('[data-bb-price-compare]');
      this.compareValueEl = this.querySelector('[data-bb-price-compare-value]');
      this.priceExEl = this.querySelector('[data-bb-price-ex]');
      this.priceIncEl = this.querySelector('[data-bb-price-inc]');
      this.modeInputs = Array.from(this.querySelectorAll('[data-bb-mode]'));
      /* Nothing is pre-selected server-side, but a back/forward restore can leave one checked. */
      this.lastWarranty = this.selectedWarranty();
      /* The theme's variant picker is rendered as a sibling of this element inside the block
         wrapper (see product-info.liquid), so ink-only mode has to reach up to hide it. */
      this.blockItem = this.closest('.product-info__block-item');
      this.ctaBase = (this.addBtn && this.addBtn.getAttribute('data-bb-cta-base')) || 'Add to cart';
      this.exGstLabel = this.getAttribute('data-ex-gst-label') || 'Ex. GST';
      this.incGstLabel = this.getAttribute('data-inc-gst-label') || 'Inc. GST';

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-bb-warranty]')) this.onWarrantyChange(e.target);
        if (e.target.matches('[data-bb-mode]')) this.onModeChange();
      });
      this.addEventListener('tis-of:change', () => this.recalculate());

      /* Printers with more than one variant get the theme's own variant picker above this box.
         The theme dispatches variant:changed on the product form with bubbles: true, so it
         reaches document — the listener the theme documents at the top of this file. The buy box
         is a descendant of that form, so it can't observe the event on itself. */
      this.onVariantChanged = (event) => this.applyVariant(event && event.detail && event.detail.variant);
      document.addEventListener('variant:changed', this.onVariantChanged);

      this.recalculate();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
    }

    applyVariant(variant) {
      if (!variant) return;

      this.printerId = variant.id;
      this.printerPrice = variant.price || 0;
      this.printerComparePrice = variant.compare_at_price || 0;
      this.printerAvailable = variant.available !== false;

      /* tis-price-block wraps each figure in its own hook element, with the "Ex. GST:" /
         "Inc. GST:" labels as siblings outside it, so replacing textContent here can't disturb
         the labels or the screen-reader text beside them. */
      this.printerTaxable = variant.taxable !== false;
      if (this.priceExEl) this.priceExEl.textContent = tisFormatMoney(tisExGstCents(this.printerPrice, this.printerTaxable, this.taxesIncluded));
      if (this.priceIncEl) this.priceIncEl.textContent = tisFormatMoney(tisIncGstCents(this.printerPrice, this.printerTaxable, this.taxesIncluded));
      this.renderSavings();
      this.syncVariantAccessories(variant.id);

      this.recalculate();
    }

    /* Only rendered when the block's "Accessories by variant" setting is on. A card for another
       variant is deselected as it hides, so a stand for the wrong size can't ride along into the
       cart unseen. */
    syncVariantAccessories(variantId) {
      const cards = this.querySelectorAll('[data-bb-for-variant]');
      if (!cards.length) return;

      cards.forEach((card) => {
        const show = card.getAttribute('data-bb-for-variant') === String(variantId);
        card.hidden = !show;
        if (!show && card.classList.contains('is-on')) {
          card.classList.remove('is-on');
          card.setAttribute('aria-pressed', 'false');
        }
      });

      if (this.accessoriesSection) {
        this.accessoriesSection.hidden = !this.querySelector('.tis-bb__acard:not([hidden])');
      }
      if (this.track) this.track.scrollLeft = 0;
    }

    onClick(e) {
      /* Before the [data-action] gate: warranty radios carry no action, and clicking the one
         that is already selected has to clear it. */
      if (e.target.matches('[data-bb-warranty]')) {
        this.onWarrantyClick(e.target);
        return;
      }

      const trigger = e.target.closest('[data-action]');
      if (!trigger || !this.contains(trigger)) return;
      const action = trigger.getAttribute('data-action');

      if (action === 'bb-accessory') {
        if (trigger.disabled) return;
        const on = trigger.classList.toggle('is-on');
        trigger.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.recalculate();
      } else if (action === 'bb-swipe') {
        if (this.track) this.track.scrollBy({ left: parseInt(trigger.getAttribute('data-dir'), 10) * 220, behavior: 'smooth' });
      } else if (action === 'bb-add') {
        this.addAll();
      } else if (action === 'request-quote') {
        this.requestQuote(trigger);
      }
    }

    /* A radio can't be unchecked by clicking it, and clicking the checked one fires no change
       event at all — so the toggle-off has to happen here, at click time.

       By the time a click listener runs the browser has already set checked, so "was it checked
       before?" can't be read off the element. this.lastWarranty remembers the previous
       selection instead. Note we must not preventDefault: for a radio that reverts to the
       pre-click state, which is exactly the state being cleared. */
    onWarrantyClick(input) {
      if (this.lastWarranty === input) {
        input.checked = false;
        this.onWarrantyChange(null);
      } else {
        this.lastWarranty = input;
      }
    }

    onWarrantyChange(input) {
      const selected = input && input.checked ? input : null;
      this.lastWarranty = selected;

      this.querySelectorAll('.tis-bb__warr').forEach((label) => label.classList.remove('is-active'));
      if (selected) {
        const label = selected.closest('.tis-bb__warr');
        if (label) label.classList.add('is-active');
      }
      this.recalculate();
    }

    /* The struck-through original beside the price, and the "Save" badge under it. Both are
       hidden outright when the variant isn't discounted. Amounts are ex GST, matching the price
       they sit against; a percentage is unaffected by GST, so it is worked from the raw prices,
       rounded the same way Liquid's `round` does. */
    renderSavings() {
      const onSale = this.printerComparePrice > this.printerPrice;

      if (this.compareEl) {
        this.compareEl.hidden = !onSale;
        if (this.compareValueEl) {
          this.compareValueEl.textContent = onSale
            ? tisFormatMoney(tisExGstCents(this.printerComparePrice, this.printerTaxable, this.taxesIncluded))
            : '';
        }
      }

      if (!this.savingsEl) return;
      this.savingsEl.hidden = !onSale;
      if (!onSale || !this.savingsValueEl) return;

      const saved = this.printerComparePrice - this.printerPrice;
      this.savingsValueEl.textContent = this.savingsEl.getAttribute('data-discount-mode') === 'percentage'
        ? `${Math.round((saved * 100) / this.printerComparePrice)}%`
        : tisFormatMoney(tisExGstCents(saved, this.printerTaxable, this.taxesIncluded));
    }

    onModeChange() {
      const inkOnly = !this.printerIncluded();

      /* Ink only strips the box back to the order form: the machine's price, warranty and
         accessories all describe a printer that isn't being bought. */
      this.classList.toggle('tis-bb--ink-only', inkOnly);
      if (this.blockItem) this.blockItem.classList.toggle('tis-bb-ink-only', inkOnly);

      this.modeInputs.forEach((input) => {
        const label = input.closest('.tis-bb__mode-option');
        if (label) label.classList.toggle('is-active', input.checked);
      });

      this.recalculate();
    }

    /* No toggle rendered (a printer with no inks to sell) means the printer is always in. */
    printerIncluded() {
      const selected = this.modeInputs.find((input) => input.checked);
      return !selected || selected.value !== 'ink';
    }

    selectedWarranty() {
      return this.querySelector('[data-bb-warranty]:checked');
    }

    recalculate() {
      const withPrinter = this.printerIncluded();
      /* The CTA total is inc GST; savings is ex GST. Both are built line by line so a GST-free
         accessory or warranty can't be taxed on the way into either figure. */
      let total = withPrinter ? tisIncGstCents(this.printerPrice, this.printerTaxable, this.taxesIncluded) : 0;
      /* Savings runs over exactly the same selections as the total, so the two can't disagree.
         A compare-at at or below the price means "not discounted", not a negative saving. */
      /* No running savings total any more: it moved with every accessory ticked, which read as
         a live total rather than a statement about the printer. The struck-through regular
         price above says the same thing about the machine itself. */
      const addLine = (el, quantity) => {
        const price = parseInt(el.getAttribute('data-price'), 10) || 0;
        const taxable = tisFlag(el, 'data-taxable', true);
        total += tisIncGstCents(price * quantity, taxable, this.taxesIncluded);
      };

      const warranty = this.selectedWarranty();
      if (warranty) addLine(warranty, 1);
      this.querySelectorAll('.tis-bb__acard.is-on').forEach((a) => addLine(a, 1));
      this.querySelectorAll('.tis-of__qty').forEach((inp) => {
        const qty = parseInt(inp.value, 10) || 0;
        if (qty > 0) addLine(inp, qty);
      });

      /* A sold-out printer says so on the button instead of failing at add time: /cart/add.js
         with an items array is atomic, so the printer line would sink the whole add. Consumables
         mode drops the printer, so the button comes back there. */
      const printerSoldOut = withPrinter && !this.printerAvailable;

      /* Inc GST, and said so — the price block above leads with the ex-GST figure. */
      if (this.ctaLabelEl) {
        this.ctaLabelEl.textContent = printerSoldOut
          ? this.soldOutLabel
          : this.ctaBase + ' — ' + tisFormatMoney(total) + ' ' + this.incGstLabel;
      }

      /* With the printer excluded and nothing else picked there is nothing to add, so the button
         says so rather than silently doing nothing when pressed. */
      if (this.addBtn && !this.addBtn.classList.contains('is-loading')) {
        this.addBtn.disabled = printerSoldOut || this.collectItems().length === 0;
      }

      /* No sold-out check: a quote for a printer that's out of stock is still a fair request. */
      if (this.quoteBtn) this.quoteBtn.disabled = this.collectItems().length === 0;

    }

    collectItems() {
      const items = [];
      if (this.printerId && this.printerIncluded()) items.push({ id: this.printerId, quantity: 1 });

      const warranty = this.selectedWarranty();
      if (warranty) {
        const wPrice = parseInt(warranty.getAttribute('data-price'), 10) || 0;
        const wId = parseInt(warranty.getAttribute('data-variant-id'), 10);
        if (wPrice > 0 && wId) items.push({ id: wId, quantity: 1 });
      }

      this.querySelectorAll('.tis-bb__acard.is-on').forEach((a) => {
        const id = parseInt(a.getAttribute('data-variant-id'), 10);
        if (id) items.push({ id: id, quantity: 1 });
      });

      this.querySelectorAll('.tis-of__qty').forEach((inp) => {
        const qty = parseInt(inp.value, 10) || 0;
        const id = parseInt(inp.getAttribute('data-variant-id'), 10);
        if (qty > 0 && id) items.push({ id: id, quantity: qty });
      });

      return items;
    }

    requestQuote(trigger) {
      const items = this.collectItems();
      if (!items.length) return;

      if (this.statusEl) this.statusEl.innerHTML = '';
      if (!tisOpenQuoteList(trigger, items)) this.showError(trigger.getAttribute('data-error'));
    }

    addAll() {
      const items = this.collectItems();
      if (!items.length) return;

      if (this.statusEl) this.statusEl.innerHTML = '';
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      fetch(this.addUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
        .then((result) => {
          this.addBtn.classList.remove('is-loading');
          /* recalculate() owns the disabled state now — it re-disables the button if the current
             configuration has nothing left to add. */
          this.recalculate();
          if (result.ok) {
            const addedQty = items.reduce((sum, item) => sum + item.quantity, 0);
            document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: addedQty, productAdded: true } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          } else {
            this.showError((result.data && result.data.description) || 'Unable to add to cart.');
          }
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        })
        .catch(() => {
          this.addBtn.classList.remove('is-loading');
          this.recalculate();
          this.showError('Unable to add to cart.');
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        });
    }

    showError(message) {
      if (this.statusEl) this.statusEl.innerHTML = '<span class="tis-of__error">' + message + '</span>';
    }
  }
  if (!customElements.get('tis-printer-buybox')) {
    customElements.define('tis-printer-buybox', TisPrinterBuybox);
  }
})();

